import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../App';

const BL_TYPES = [
  { value: 'ocean_bl',    label: 'Ocean BL',    icon: '🚢', color: '#1d4ed8', bg: '#eff6ff', border: '#93c5fd' },
  { value: 'house_bl',    label: 'House BL',    icon: '🏠', color: '#15803d', bg: '#f0fdf4', border: '#86efac' },
  { value: 'seaway_bill', label: 'Seaway Bill',  icon: '📜', color: '#7c3aed', bg: '#f5f3ff', border: '#c4b5fd' },
];

const STATUS_META = {
  draft:             { label: 'Entwurf',            bg: '#f8fafc', border: '#cbd5e1', text: '#64748b' },
  issued:            { label: 'Ausgestellt',         bg: '#eff6ff', border: '#93c5fd', text: '#1d4ed8' },
  surrendered:       { label: 'Surrendered',         bg: '#fefce8', border: '#fde047', text: '#a16207' },
  telex_released:    { label: 'Telex Released',      bg: '#f0fdf4', border: '#86efac', text: '#15803d' },
  original_released: { label: 'Original Released',   bg: '#d1fae5', border: '#6ee7b7', text: '#065f46' },
  cancelled:         { label: 'Storniert',            bg: '#fef2f2', border: '#fca5a5', text: '#dc2626' },
};

const RELEASE_TYPES = [
  { value: 'original',        label: 'Original BL' },
  { value: 'telex_release',   label: 'Telex Release' },
  { value: 'seaway_bill',     label: 'Seaway Bill' },
  { value: 'express_release', label: 'Express Release' },
];

const FREIGHT_TERMS = [
  { value: 'prepaid', label: 'Freight Prepaid' },
  { value: 'collect', label: 'Freight Collect' },
];

const UNITS = ['MT', 'BBL', 'KL', 'CBM', 'other'];

function blTypeMeta(v) { return BL_TYPES.find(t => t.value === v) || BL_TYPES[0]; }

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.draft;
  return <span style={{ padding: '2px 10px', borderRadius: 99, background: m.bg, border: `1px solid ${m.border}`, color: m.text, fontSize: 11, fontWeight: 600 }}>{m.label}</span>;
}

function BLTypeBadge({ blType }) {
  const m = blTypeMeta(blType);
  return <span style={{ padding: '2px 10px', borderRadius: 99, background: m.bg, border: `1px solid ${m.border}`, color: m.color, fontSize: 11, fontWeight: 600 }}>{m.icon} {m.label}</span>;
}

const EMPTY_FORM = {
  title: '', deal_reference: '', bl_number: '', bl_type: 'ocean_bl', status: 'draft',
  issue_date: '', port_of_loading: '', port_of_discharge: '', vessel_name: '', voyage_number: '',
  shipper_name: '', consignee_name: '', consignee_address: '', notify_party: '', notify_party_address: '',
  cargo_description: '', quantity: '', unit: 'MT', gross_weight: '',
  freight_terms: '', release_type: '', release_instructions: '', original_copies: '3',
  shipping_line: '', freight_forwarder: '', notes: '', tags: [],
};

const fieldStyle = { width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-surface)', color: 'var(--text1)', fontSize: 13, boxSizing: 'border-box' };
const labelStyle = { fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 4, display: 'block' };
const groupStyle = { display: 'flex', flexDirection: 'column', gap: 2 };

function BOLForm({ initial, onSave, onCancel, loading }) {
  const [form, setForm] = useState(() => {
    if (!initial) return EMPTY_FORM;
    return {
      ...EMPTY_FORM, ...initial,
      tags: Array.isArray(initial.tags) ? initial.tags.join(', ') : (initial.tags || ''),
    };
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inp = (k) => ({ value: form[k] || '', onChange: e => set(k, e.target.value) });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) return alert('Titel erforderlich');
    const payload = {
      ...form,
      tags: typeof form.tags === 'string'
        ? form.tags.split(',').map(t => t.trim()).filter(Boolean)
        : (form.tags || []),
    };
    onSave(payload);
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div style={{ ...groupStyle, gridColumn: '1 / -1' }}>
          <label style={labelStyle}>Titel *</label>
          <input style={fieldStyle} {...inp('title')} placeholder="z.B. BL NLCO Crude, MOH Deal 2026-01" required />
        </div>
        <div style={groupStyle}>
          <label style={labelStyle}>BL-Nummer</label>
          <input style={fieldStyle} {...inp('bl_number')} placeholder="z.B. MSCU1234567" />
        </div>
        <div style={groupStyle}>
          <label style={labelStyle}>Deal Reference</label>
          <input style={fieldStyle} {...inp('deal_reference')} placeholder="z.B. RMDG-2026-001" />
        </div>
        <div style={groupStyle}>
          <label style={labelStyle}>BL-Typ</label>
          <select style={fieldStyle} value={form.bl_type} onChange={e => set('bl_type', e.target.value)}>
            {BL_TYPES.map(t => <option key={t.value} value={t.value}>{t.icon} {t.label}</option>)}
          </select>
        </div>
        <div style={groupStyle}>
          <label style={labelStyle}>Status</label>
          <select style={fieldStyle} value={form.status} onChange={e => set('status', e.target.value)}>
            {Object.entries(STATUS_META).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
          </select>
        </div>
        <div style={groupStyle}>
          <label style={labelStyle}>Ausstellungsdatum</label>
          <input type="date" style={fieldStyle} {...inp('issue_date')} />
        </div>
        <div style={groupStyle}>
          <label style={labelStyle}>Originale (Anzahl)</label>
          <input style={fieldStyle} {...inp('original_copies')} placeholder="3" />
        </div>
      </div>

      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>Handelsroute & Vessel</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={groupStyle}><label style={labelStyle}>Port of Loading (POL)</label><input style={fieldStyle} {...inp('port_of_loading')} placeholder="z.B. Bonny Terminal, Nigeria" /></div>
          <div style={groupStyle}><label style={labelStyle}>Port of Discharge (POD)</label><input style={fieldStyle} {...inp('port_of_discharge')} placeholder="z.B. Agioi Theodoroi, Greece" /></div>
          <div style={groupStyle}><label style={labelStyle}>Vessel Name</label><input style={fieldStyle} {...inp('vessel_name')} placeholder="z.B. MT AGIOS SPYRIDON" /></div>
          <div style={groupStyle}><label style={labelStyle}>Voyage Number</label><input style={fieldStyle} {...inp('voyage_number')} placeholder="z.B. 2601N" /></div>
        </div>
      </div>

      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>Parteien</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={groupStyle}><label style={labelStyle}>Shipper</label><input style={fieldStyle} {...inp('shipper_name')} /></div>
          <div style={groupStyle}><label style={labelStyle}>Consignee</label><input style={fieldStyle} {...inp('consignee_name')} placeholder="z.B. Motor Oil Hellas oder TO ORDER" /></div>
          <div style={{ ...groupStyle, gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Consignee Adresse</label>
            <textarea style={{ ...fieldStyle, minHeight: 60, resize: 'vertical' }} {...inp('consignee_address')} />
          </div>
          <div style={groupStyle}><label style={labelStyle}>Notify Party</label><input style={fieldStyle} {...inp('notify_party')} /></div>
          <div style={groupStyle}><label style={labelStyle}>Notify Party Adresse</label><input style={fieldStyle} {...inp('notify_party_address')} /></div>
        </div>
      </div>

      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>Ladung</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={{ ...groupStyle, gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Cargo-Beschreibung</label>
            <textarea style={{ ...fieldStyle, minHeight: 56, resize: 'vertical' }} {...inp('cargo_description')} placeholder="z.B. Nigerian Light Crude Oil (NLCO)" />
          </div>
          <div style={groupStyle}><label style={labelStyle}>Menge</label><input style={fieldStyle} {...inp('quantity')} placeholder="z.B. 600000" /></div>
          <div style={groupStyle}>
            <label style={labelStyle}>Einheit</label>
            <select style={fieldStyle} value={form.unit || 'MT'} onChange={e => set('unit', e.target.value)}>
              {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
          <div style={groupStyle}><label style={labelStyle}>Bruttogewicht</label><input style={fieldStyle} {...inp('gross_weight')} placeholder="z.B. 600,000 MT" /></div>
        </div>
      </div>

      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>Fracht & Release</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={groupStyle}>
            <label style={labelStyle}>Freight Terms</label>
            <select style={fieldStyle} value={form.freight_terms || ''} onChange={e => set('freight_terms', e.target.value)}>
              <option value="">Bitte wahlen</option>
              {FREIGHT_TERMS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </div>
          <div style={groupStyle}>
            <label style={labelStyle}>Release-Typ</label>
            <select style={fieldStyle} value={form.release_type || ''} onChange={e => set('release_type', e.target.value)}>
              <option value="">Bitte wahlen</option>
              {RELEASE_TYPES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          <div style={{ ...groupStyle, gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Release Instructions</label>
            <textarea style={{ ...fieldStyle, minHeight: 70, resize: 'vertical' }} {...inp('release_instructions')} />
          </div>
        </div>
      </div>

      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={groupStyle}><label style={labelStyle}>Shipping Line / Carrier</label><input style={fieldStyle} {...inp('shipping_line')} placeholder="z.B. MSC, Maersk" /></div>
          <div style={groupStyle}><label style={labelStyle}>Freight Forwarder</label><input style={fieldStyle} {...inp('freight_forwarder')} /></div>
          <div style={{ ...groupStyle, gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Notizen</label>
            <textarea style={{ ...fieldStyle, minHeight: 56, resize: 'vertical' }} {...inp('notes')} />
          </div>
          <div style={{ ...groupStyle, gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Tags (kommagetrennt)</label>
            <input style={fieldStyle} {...inp('tags')} placeholder="z.B. MOH, crude, urgent" />
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 4 }}>
        <button type="button" onClick={onCancel} disabled={loading}
          style={{ padding: '9px 20px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text2)', fontSize: 13, cursor: 'pointer' }}>
          Abbrechen
        </button>
        <button type="submit" disabled={loading}
          style={{ padding: '9px 20px', borderRadius: 6, border: 'none', background: loading ? '#16a34a' : '#22c55e', color: '#000', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          {loading ? 'Speichern...' : 'Speichern'}
        </button>
      </div>
    </form>
  );
}

export default function BillOfLadingPage() {
  const [bols, setBols]       = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving]   = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType]     = useState('');
  const [filterDeal, setFilterDeal]     = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterType)   params.bl_type = filterType;
      if (filterDeal)   params.deal_reference = filterDeal;
      const [bolsRes, summaryRes] = await Promise.all([
        API.get('/bill-of-lading/', { params }),
        API.get('/bill-of-lading/summary'),
      ]);
      setBols(bolsRes.data);
      setSummary(summaryRes.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterType, filterDeal]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSave = async (payload) => {
    setSaving(true); setError('');
    try {
      if (editItem) {
        await API.patch(`/bill-of-lading/${editItem.id}`, payload);
      } else {
        await API.post('/bill-of-lading/', payload);
      }
      setShowForm(false); setEditItem(null);
      await fetchAll();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bill of Lading loschen?')) return;
    try {
      await API.delete(`/bill-of-lading/${id}`);
      await fetchAll();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Fehler beim Loschen');
    }
  };

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '20px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text1)', margin: 0 }}>📄 Bill of Lading Tracker</h1>
          <p style={{ fontSize: 13, color: 'var(--text3)', margin: '4px 0 0' }}>BL-Status, Ocean/House BL, Consignee Details, Release Instructions</p>
        </div>
        {!showForm && (
          <button onClick={() => { setEditItem(null); setShowForm(true); }}
            style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: '#22c55e', color: '#000', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            + Neues BL
          </button>
        )}
      </div>

      {error && <div style={{ padding: '10px 14px', background: '#ef444415', border: '1px solid #ef444440', borderRadius: 8, color: '#ef4444', fontSize: 13, marginBottom: 16 }}>{error}</div>}

      {/* Summary */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 20 }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px', textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#22c55e' }}>{summary.total}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>Gesamt</div>
          </div>
          {Object.entries(summary.by_status || {}).map(([s, cnt]) => {
            const m = STATUS_META[s] || { label: s, text: 'var(--text3)' };
            return (
              <div key={s} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px', textAlign: 'center' }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: m.text }}>{cnt}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{m.label}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: 20, marginBottom: 20 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text1)', margin: '0 0 16px' }}>
            {editItem ? 'Bill of Lading bearbeiten' : 'Neues Bill of Lading'}
          </h2>
          <BOLForm initial={editItem} onSave={handleSave} onCancel={() => { setShowForm(false); setEditItem(null); }} loading={saving} />
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 12, color: 'var(--text1)', background: 'var(--bg-card)' }}>
          <option value="">Alle Status</option>
          {Object.entries(STATUS_META).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
        </select>
        <select value={filterType} onChange={e => setFilterType(e.target.value)}
          style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 12, color: 'var(--text1)', background: 'var(--bg-card)' }}>
          <option value="">Alle Typen</option>
          {BL_TYPES.map(t => <option key={t.value} value={t.value}>{t.icon} {t.label}</option>)}
        </select>
        <input value={filterDeal} onChange={e => setFilterDeal(e.target.value)}
          placeholder="Deal Reference filtern..."
          style={{ padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 12, color: 'var(--text1)', background: 'var(--bg-card)', minWidth: 180 }} />
        <button onClick={fetchAll}
          style={{ padding: '7px 14px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text2)', fontSize: 12, cursor: 'pointer' }}>
          Aktualisieren
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)', fontSize: 14 }}>Laden...</div>
      ) : bols.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: 'var(--text3)' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📄</div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>Keine Bills of Lading gefunden</div>
          <div style={{ fontSize: 13, marginTop: 6 }}>Erstellen Sie das erste BL.</div>
        </div>
      ) : (
        <div>
          {bols.map(bol => (
            <div key={bol.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text1)', marginBottom: 6, wordBreak: 'break-word' }}>{bol.title}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                    <StatusBadge status={bol.status} />
                    <BLTypeBadge blType={bol.bl_type} />
                    {bol.deal_reference && (
                      <span style={{ padding: '2px 10px', borderRadius: 99, background: '#22c55e20', border: '1px solid #22c55e40', color: '#22c55e', fontSize: 11 }}>
                        📋 {bol.deal_reference}
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '4px 16px', fontSize: 12, color: 'var(--text2)' }}>
                    {bol.bl_number && <span><strong>BL-Nr:</strong> {bol.bl_number}</span>}
                    {bol.issue_date && <span><strong>Ausgestellt:</strong> {bol.issue_date}</span>}
                    {bol.vessel_name && <span><strong>Vessel:</strong> {bol.vessel_name}{bol.voyage_number ? ` / Voy ${bol.voyage_number}` : ''}</span>}
                    {bol.port_of_loading && <span><strong>POL:</strong> {bol.port_of_loading}</span>}
                    {bol.port_of_discharge && <span><strong>POD:</strong> {bol.port_of_discharge}</span>}
                    {bol.consignee_name && <span><strong>Consignee:</strong> {bol.consignee_name}</span>}
                    {bol.quantity && <span><strong>Menge:</strong> {bol.quantity} {bol.unit || ''}</span>}
                    {bol.freight_terms && <span><strong>Freight:</strong> {FREIGHT_TERMS.find(f => f.value === bol.freight_terms)?.label || bol.freight_terms}</span>}
                    {bol.release_type && <span><strong>Release:</strong> {RELEASE_TYPES.find(r => r.value === bol.release_type)?.label || bol.release_type}</span>}
                  </div>
                  {bol.notes && <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text3)', fontStyle: 'italic' }}>{bol.notes}</div>}
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button onClick={() => { setEditItem(bol); setShowForm(true); }}
                    style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text2)', fontSize: 12, cursor: 'pointer' }}>
                    Bearbeiten
                  </button>
                  <button onClick={() => handleDelete(bol.id)}
                    style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #ef444440', background: '#ef444415', color: '#ef4444', fontSize: 12, cursor: 'pointer' }}>
                    🗑
                  </button>
                </div>
              </div>
            </div>
          ))}
          <div style={{ fontSize: 12, color: 'var(--text3)', textAlign: 'right', marginTop: 8 }}>
            {bols.length} Eintrag{bols.length !== 1 ? 'e' : ''}
          </div>
        </div>
      )}
    </div>
  );
}
